import * as THREE from 'three';

import { FACTION_COLOR, type Faction } from '../core/constants.ts';
import { isEmbodied, type PlayerState } from '../core/player.ts';

/**
 * Names floating above teammates.
 *
 * Drawn as sprites rather than as HTML boxes tracked against projected
 * coordinates. Two dozen absolutely-positioned elements being moved every frame
 * is two dozen layout invalidations a frame, and the browser will make you pay
 * for that long before the GPU notices twenty-four quads.
 *
 * Sprites also get depth testing for free, which is the behaviour you want: a
 * name is occluded by the pillar its owner is standing behind, so a nameplate
 * never tells you about somebody you cannot see.
 */

/** How high above a mage's feet the plate rides. Clear of the tallest hat. */
const PLATE_HEIGHT = 2.15;
/** Names stop being drawn past this. Past it they are unreadable anyway. */
const PLATE_DISTANCE = 55;
/**
 * Deliberately faint. A name is a thing you glance at, and eight of them at full
 * strength would compete with the spells they are standing among.
 */
const PLATE_OPACITY = 0.5;

/** Rendered once per name at this height, then scaled down in the world. */
const TEXTURE_HEIGHT = 64;

export class Nameplates {
  private readonly scene: THREE.Scene;
  private readonly plates = new Map<string, THREE.Sprite>();
  /** What each plate was last drawn as, so a canvas is not redrawn per frame. */
  private readonly drawn = new Map<string, string>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Draws a name to a texture.
   *
   * Once per name per state, not per frame: rasterising text is expensive and a
   * name changes only when its owner dies or comes back.
   */
  private texture(name: string, colour: number): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context for a nameplate');

    const font = `600 ${TEXTURE_HEIGHT * 0.62}px "Inter", system-ui, sans-serif`;
    context.font = font;
    const width = Math.ceil(context.measureText(name).width) + 24;

    canvas.width = width;
    canvas.height = TEXTURE_HEIGHT;

    // Re-set after resizing: changing a canvas's dimensions resets its context.
    context.font = font;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // A dark outline rather than a filled plate behind the text. The arena is
    // dark and mostly warm, and a box would read as a UI element sitting in the
    // world where the name should read as belonging to the mage.
    context.lineWidth = 6;
    context.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    context.strokeText(name, width / 2, TEXTURE_HEIGHT / 2);

    context.fillStyle = `#${colour.toString(16).padStart(6, '0')}`;
    context.fillText(name, width / 2, TEXTURE_HEIGHT / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /**
   * Builds the plates the match will actually want, during loading.
   *
   * The roster is known before the first frame, so there is no reason for the
   * first teammate to walk into range and pay for their own nameplate. Each one
   * costs a canvas, a font shaping pass and a texture upload, and the *first*
   * one also costs the `SpriteMaterial` program — which is why the hitch landed
   * a second or two into a match rather than at the first spell.
   *
   * Only the living state is built. A plate is redrawn when its owner goes down,
   * but by then the program exists and a redraw is one small canvas — under a
   * fifth of a millisecond, and not something anybody has ever felt. It is the
   * first one that costs.
   *
   * Plates are keyed by player id and re-keyed by `${name}|${down}`, so building
   * them here is not a special case that `update` has to know about: it finds
   * them in the map, agrees with what is drawn, and leaves them alone.
   */
  warmUp(
    // Id, name and side are all a nameplate needs, and all a roster carries —
    // which is what this is warmed from, because the player view is filtered by
    // relevancy and at load time holds nobody but you.
    players: readonly { id: string; name: string; faction: Faction }[],
    localId: string,
    at: THREE.Vector3,
  ): number {
    // No DOM, no canvas to rasterise a name onto. See `DamageNumbers.warmUp`.
    if (typeof document === 'undefined') return 0;

    const me = players.find((p) => p.id === localId);
    if (!me) return 0;

    let built = 0;
    for (const p of players) {
      if (p.id === localId) continue;
      if (p.faction !== me.faction) continue;

      const texture = this.texture(p.name, FACTION_COLOR[p.faction]);
      const plate = new THREE.Sprite(new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: PLATE_OPACITY,
        depthWrite: false,
      }));
      const aspect = texture.image.width / texture.image.height;
      plate.scale.set(0.34 * aspect, 0.34, 1);
      // Where the warm frames can see it. `update` moves it the moment the match
      // starts, and hides it if its owner is out of range.
      plate.position.copy(at);
      plate.frustumCulled = false;
      this.scene.add(plate);
      this.plates.set(p.id, plate);
      this.drawn.set(p.id, `${p.name}|false`);
      built++;
    }
    return built;
  }

  /** Plates back out of sight. `update` puts up whichever ones belong. */
  coolDown(): void {
    for (const plate of this.plates.values()) {
      plate.visible = false;
      // Culling was off only so the warm frames could not skip them. In play a
      // plate behind the camera should cost nothing.
      plate.frustumCulled = true;
    }
  }

  /**
   * Mirrors the roster: a plate for every living teammate, and none for anybody
   * else.
   *
   * Enemy names are not drawn at all. Knowing where somebody is, through a wall
   * or across a room, is the thing a shooter asks you to work out.
   */
  update(players: readonly PlayerState[], localId: string, camera: THREE.Camera): void {
    const me = players.find((p) => p.id === localId);
    if (!me) return;

    const wanted = new Set<string>();

    for (const p of players) {
      if (p.id === localId) continue;
      if (p.faction !== me.faction) continue;
      if (p.inValhalla) continue;

      const distance = camera.position.distanceTo(
        new THREE.Vector3(p.pos.x, p.pos.y, p.pos.z),
      );
      if (distance > PLATE_DISTANCE) continue;
      wanted.add(p.id);

      // Grey once they are down, and the same faction colour while they are up.
      // A teammate's name going grey is the fastest possible way to be told they
      // need calling back.
      const down = !isEmbodied(p);
      const key = `${p.name}|${down}`;

      let plate = this.plates.get(p.id);
      if (!plate || this.drawn.get(p.id) !== key) {
        const texture = this.texture(p.name, down ? 0x9a958c : FACTION_COLOR[p.faction]);

        if (plate) {
          // The old texture is this sprite's alone, so it goes. Materials hold
          // programs and are kept; a canvas texture is memory and is not shared.
          (plate.material.map as THREE.Texture | null)?.dispose();
          plate.material.map = texture;
          plate.material.needsUpdate = true;
        } else {
          plate = new THREE.Sprite(new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: PLATE_OPACITY,
            depthWrite: false,
          }));
          this.scene.add(plate);
          this.plates.set(p.id, plate);
        }

        // Scaled from the texture so a long name is wider rather than squashed.
        const aspect = texture.image.width / texture.image.height;
        plate.scale.set(0.34 * aspect, 0.34, 1);
        this.drawn.set(p.id, key);
      }

      plate.visible = true;
      plate.position.set(p.pos.x, p.pos.y + PLATE_HEIGHT, p.pos.z);
    }

    for (const [id, plate] of this.plates) {
      if (!wanted.has(id)) plate.visible = false;
    }
  }

  dispose(): void {
    for (const plate of this.plates.values()) {
      this.scene.remove(plate);
      (plate.material.map as THREE.Texture | null)?.dispose();
      plate.material.dispose();
    }
    this.plates.clear();
    this.drawn.clear();
  }
}
